import React, { useRef, useEffect, useState } from 'react';
import { Box, IconButton, Fade, Portal } from '@mui/material';
import { Close } from '@mui/icons-material';

const CornerVideoPopup = ({ 
    open, 
    onClose, 
    onVideoClick, 
    size = { width: 200, height: 300 }
}) => {
    const videoRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [playAttempted, setPlayAttempted] = useState(false);

    useEffect(() => {
        if (open && videoRef.current && isLoaded) {
            // Start muted autoplay with loop when popup opens
            videoRef.current.muted = true;
            videoRef.current.loop = true;
            videoRef.current.defaultMuted = true;
            
            // Try to play with better error handling for mobile
            if (!playAttempted) {
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.then(() => {
                        setPlayAttempted(true);
                    }).catch((error) => {
                        console.log('Autoplay was prevented:', error);
                        // On mobile, autoplay might be blocked, but video will show poster
                    });
                }
            }
        }
    }, [open, isLoaded]);

    const handleVideoClick = () => {
        if (videoRef.current) {
            videoRef.current.pause();
        }
        onVideoClick();
    };

    const handleClose = (e) => {
        e.stopPropagation();
        if (videoRef.current) {
            videoRef.current.pause();
        }
        onClose();
    };

    const handleVideoLoaded = () => {
        setIsLoaded(true);
    };

    const handlePopupInteraction = () => {
        // Try to play video on first user interaction (helps with mobile autoplay restrictions)
        if (videoRef.current && !playAttempted) {
            videoRef.current.muted = true;
            videoRef.current.play().catch(console.error);
        }
    };

    if (!open) return null;

    return (
        <Portal>
            <Fade in={open} timeout={500}>
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        width: size.width,
                        height: size.height,
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                        border: '2px solid #fff',
                        cursor: 'pointer',
                        zIndex: 1400,
                        backgroundColor: '#000',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 12px 48px rgba(0,0,0,0.4)'
                        }
                    }}
                    onClick={handleVideoClick}
                    onTouchStart={handlePopupInteraction}
                    onMouseEnter={handlePopupInteraction}
                >
                    {/* Close button */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 2,
                            zIndex: 1
                        }}
                    >
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            width: 20,
                            height: 20,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)'
                            }
                        }}
                    >
                        <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>

                {/* Video element */}
                <video
                    ref={videoRef}
                    width="100%"
                    height="100%"
                    poster={`${process.env.PUBLIC_URL}/assets/images/Headofschool-Intro-poster.jpg`}
                    preload="metadata"
                    playsInline
                    webkit-playsinline="true"
                    autoPlay
                    muted
                    defaultMuted
                    loop
                    onCanPlay={handleVideoLoaded}
                    style={{
                        display: 'block',
                        objectFit: 'cover',
                        width: '100%',
                        height: '100%'
                    }}
                >
                    <source src={`${process.env.PUBLIC_URL}/assets/videos/Headofschool-Intro.mp4`} type="video/mp4" />
                </video>

                {/* Overlay to indicate it's clickable */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
                        color: 'white',
                        padding: 1,
                        fontSize: '0.75rem',
                        textAlign: 'center',
                        pointerEvents: 'none'
                    }}
                >
                        Click to watch full video
                    </Box>
                </Box>
            </Fade>
        </Portal>
    );
};

export default CornerVideoPopup;